/**
 * Mock data for Performance Analysis UI.
 * Toggle via USE_PERFORMANCE_ANALYSIS_MOCK — true uses mocks, false hits the real API.
 */
import { DateTime } from 'luxon';
import { of, Observable } from 'rxjs';
import { delay } from 'rxjs/operators';
import { JobStatus } from '../../../enums/job-status.enum';
import { PerformanceAnalysisJobFilter } from '../../../enums/performance-analysis-job-filter.enum';
import { PerformanceAnalysisPeriod } from '../../../enums/performance-analysis-period.enum';
import { SlotType } from '../../../enums/slot-type.enum';
import {
  PerformanceAnalysisData,
  PerformanceAnalysisFilters,
  PerformanceAnalysisQuery,
} from '../../../interfaces/performance-analysis.interface';

/** Set to false to use the real Performance Analysis API. */
export const USE_PERFORMANCE_ANALYSIS_MOCK = false;

const MOCK_DELAY_MS = 10000;

function buildTrendPoints(days: number): { date: string; value: number }[] {
  const today = DateTime.now().startOf('day');
  const points: { date: string; value: number }[] = [];

  for (let dayOffset = days - 1; dayOffset >= 0; dayOffset--) {
    const date = today.minus({ days: dayOffset });
    const base = 8 + Math.round((days - dayOffset) * 0.35);
    const wobble = (dayOffset % 5) - 2;
    points.push({
      date: date.toISODate() ?? date.toFormat('yyyy-MM-dd'),
      value: Math.max(0, base + wobble),
    });
  }

  return points;
}

export function getMockPerformanceAnalysisFilters(): Observable<PerformanceAnalysisFilters> {
  const filters: PerformanceAnalysisFilters = {
    jobFilters: [
      { type: PerformanceAnalysisJobFilter.ALL_JOBS, label: '' },
      { type: PerformanceAnalysisJobFilter.ALL_OPEN_JOBS, label: '' },
      { type: PerformanceAnalysisJobFilter.ALL_BASIC_JOBS, label: '' },
      { type: PerformanceAnalysisJobFilter.ALL_AGENTS_JOBS, label: '' },
    ],
    jobs: [
      {
        id: 101,
        title: 'Senior Frontend Engineer (m/w/d)',
        status: JobStatus.OPEN,
        slotType: SlotType.ACTIVE_SOURCING,
      },
      {
        id: 102,
        title: 'Recruiter (m/w/d)',
        status: JobStatus.OPEN,
        slotType: SlotType.BASIC,
      },
      {
        id: 103,
        title: 'Werkstudent Marketing',
        status: JobStatus.CLOSED,
        slotType: SlotType.BASIC,
      },
    ],
    periodOptions: Object.values(PerformanceAnalysisPeriod).map((type) => ({
      type,
      label: '',
    })),
  };

  return of(filters).pipe(delay(MOCK_DELAY_MS));
}

export function getMockPerformanceAnalysis(
  query: PerformanceAnalysisQuery,
): Observable<PerformanceAnalysisData> {
  const periodDays =
    query.period === PerformanceAnalysisPeriod.LAST_7_DAYS
      ? 7
      : query.period === PerformanceAnalysisPeriod.LAST_60_DAYS
        ? 60
        : query.period === PerformanceAnalysisPeriod.LAST_90_DAYS
          ? 90
          : 30;

  // Empty chart scenario for last 7 days — useful to verify empty placeholders.
  const emptyCharts = query.period === PerformanceAnalysisPeriod.LAST_7_DAYS;

  const periodEnd = DateTime.now().startOf('day');
  const periodStart = periodEnd.minus({ days: periodDays - 1 });

  const data: PerformanceAnalysisData = {
    meta: {
      isReady: true,
      daysUntilReady: null,
      hasDataForPeriod: true,
      periodStartsBeforeAccount: false,
      hasAtsIntegration: true,
      hasAgentsPackage: false,
      periodStart: periodStart.toISODate() ?? '',
      periodEnd: periodEnd.toISODate() ?? '',
    },
    funnel: {
      jobViews: {
        value: 12480,
        funnelContextPercent: null,
        advantagePercent: 42,
      },
      screenedProfiles: {
        value: 1860,
        funnelContextPercent: 14.9,
        advantagePercent: 68,
      },
      topCandidates: {
        value: 214,
        funnelContextPercent: 11.5,
        advantagePercent: 35,
      },
      timeSavedHours: {
        value: 96.5,
        funnelContextPercent: null,
        advantagePercent: 55,
      },
    },
    insights: {
      screenedTalentsTrend: {
        dataPoints: emptyCharts
          ? []
          : buildTrendPoints(Math.min(periodDays, 30)),
        trendPercent: emptyCharts ? null : 18,
        trendPositive: !emptyCharts,
        trendComparisonDays: 7,
      },
      channelEfficiency: {
        channels: emptyCharts
          ? []
          : [
              { channel: 'Jobboard', count: 420 },
              { channel: 'Meta Ads', count: 280 },
              { channel: 'LinkedIn', count: 195 },
              { channel: 'Agents', count: 110 },
              { channel: 'Karriereseite', count: 85 },
            ],
        showAgentsTip: true,
      },
    },
    recruitingMetrics: {
      totalApplications: 642,
      invitations: 78,
      timeToResponse: 2.4,
      timeToInvitation: 5.1,
      timeToHire: 18.6,
    },
  };

  return of(data).pipe(delay(MOCK_DELAY_MS));
}

export function getMockPerformanceAnalysisCsv(): Observable<{ csv: string }> {
  const csv = [
    'metric,value',
    'jobViews,12480',
    'screenedProfiles,1860',
    'topCandidates,214',
    'timeSavedHours,96.5',
  ].join('\n');

  return of({ csv }).pipe(delay(MOCK_DELAY_MS));
}

export function getMockPerformanceAnalysisPdf(): Observable<Blob> {
  const blob = new Blob(['Mock Performance Analysis PDF'], {
    type: 'application/pdf',
  });
  return of(blob).pipe(delay(MOCK_DELAY_MS));
}
